import { Check } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { JOB_BUILDER_SECTION_LABELS, type JobBuilderSection, type JobSectionState } from '@nest-recruit/shared';
import styles from './builder.module.css';

/**
 * JOB-13's section list: each section a real link (so it opens in a new tab), ticked when
 * complete, else how many things it still needs. A switched-off module's section isn't drawn
 * (QoL §13). A plain click swaps the section without a page load, as the stage bar does.
 */
export function SectionNav({
  sections,
  current,
  hrefOf,
  onSelect,
}: {
  readonly sections: readonly JobSectionState[];
  readonly current: JobBuilderSection;
  readonly hrefOf: (section: JobBuilderSection) => string;
  readonly onSelect: (section: JobBuilderSection) => void;
}): ReactNode {
  function onClick(event: MouseEvent<HTMLAnchorElement>, section: JobBuilderSection): void {
    // Anything but a plain left click is the browser's: Ctrl-click still opens a new tab.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onSelect(section);
  }

  return (
    <nav className={styles.nav} aria-label="Job setup">
      <ol className={styles.navList}>
        {sections
          .filter((state) => state.isShown)
          .map((state) => (
            <li key={state.section}>
              <a
                href={hrefOf(state.section)}
                className={styles.navLink}
                aria-current={state.section === current ? 'page' : undefined}
                onClick={(event) => onClick(event, state.section)}
              >
                <span className={styles.navMark} data-complete={state.isComplete || undefined} aria-hidden>
                  {state.isComplete ? <Check size={13} /> : state.missing.length}
                </span>
                <span className={styles.navLabel}>{JOB_BUILDER_SECTION_LABELS[state.section]}</span>
                <span className="sr-only">{state.isComplete ? ', complete' : `, ${state.missing.length} to do`}</span>
              </a>
            </li>
          ))}
      </ol>
    </nav>
  );
}
